import { Overlay } from '@eti/components';
import { useTranslation } from '@eti/providers';
import type { BoundNew, Size3d, TripType } from '@eti/schema-types';
import type { AncillaryBundleOptionsFeatureType } from '../../../../common/utils/models';
import type { AvailableExtraProductsType } from '../v3/branded-fares/model';
import EtgFaresDrawerContent from './EtgFaresDrawerContent';

export interface EtgFaresDrawerProps {
  availableExtraProducts: AvailableExtraProductsType[];
  bounds: BoundNew[];
  dataTestId: string;
  handleClose: () => void;
  isOpen: boolean;
  sellableOptions: AncillaryBundleOptionsFeatureType[];
  title: string;
  tripType: TripType;
}

const EtgFaresDrawer = ({
  availableExtraProducts,
  bounds,
  dataTestId,
  handleClose,
  isOpen,
  sellableOptions,
  title,
  tripType,
}: EtgFaresDrawerProps) => {
  const { t } = useTranslation();
  const generateOptionKey = (index: string) => `option_${index}`;

  const getPiecesAndWeight = (
    pieces: number | null,
    weight: number | null,
    weightUnit: string | null,
  ) =>
    pieces && weight && weightUnit
      ? `${t('Product.BaggagePerTraveler.Pieces', pieces)} ${t(
          'Product.BaggagePerTraveler.Weight',
          weight,
          weightUnit,
        )}`
      : null;

  const getDimensions = ({ width, height, length }: Size3d) =>
    t('Product.CabinBaggagePerTraveler.Dimensions', width, height, length);

  return (
    <Overlay.Drawer
      dataTestId={dataTestId}
      handleClose={handleClose}
      id="product-etg-info-drawer"
      isOpen={isOpen}
    >
      <Overlay.Header onClose={handleClose} title={title} />
      <Overlay.Content data-testid={`${dataTestId}-content`}>
        {sellableOptions?.map((feature: AncillaryBundleOptionsFeatureType, index: number) => (
          <EtgFaresDrawerContent
            availableExtraProducts={availableExtraProducts}
            bounds={bounds}
            description={feature.description}
            dimensions={feature.size3d ? getDimensions(feature.size3d) : null}
            id={index}
            key={generateOptionKey(`product-bundle_tab_feature_${index}`)}
            name={feature.name}
            piecesAndWeight={getPiecesAndWeight(feature.pieces, feature.weight, feature.weightUnit)}
            productTypeId={feature.productTypeId}
            readMoreText={feature.readMoreText}
            termsUrl={feature.termsUrl}
            tripType={tripType}
          />
        ))}
      </Overlay.Content>
    </Overlay.Drawer>
  );
};

export default EtgFaresDrawer;
